import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider
} from '@mui/material';
import { useBillingInvoicing } from '../context/BillingInvoicingContext';
import { useTranslation } from 'react-i18next';
import useAuth from '../../../hooks/useAuth';
import {
  getApprovalThreshold,
  fetchInvoiceCorrections,
  createInvoiceCorrection,
  InvoiceCorrectionDto,
  fetchEligibleCorrectionInvoices,
  finalizeInvoice,
  sendInvoice,
  emailInvoice
} from '../api/billingInvoicingApi';
import Autocomplete from '@mui/material/Autocomplete';
import Snackbar from '@mui/material/Snackbar';
import { useNavigate } from 'react-router-dom';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { formatBillingItemDescription } from '../utils/descriptionFormatter';
import { invoiceLogoBase64 } from '../../../assets/invoiceLogoBase64';
pdfMake.vfs = pdfFonts.vfs;

const InvoiceDetails: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    invoice,
    selectedCompany,
    approveInvoice,
    rejectInvoice,
    approveLoading,
    rejectLoading,
    approveError,
    rejectError,
    setCurrentStep,
    setInvoice
  } = useBillingInvoicing();
  const { user } = useAuth();
  const approverId = user?.userProfile?.id;
  const navigate = useNavigate();

  // All hooks at the top level
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [approvalThreshold, setApprovalThreshold] = useState<number | null>(
    null
  );
  const [thresholdLoading, setThresholdLoading] = useState(true);
  const [corrections, setCorrections] = useState<InvoiceCorrectionDto[]>([]);
  const [loadingCorrections, setLoadingCorrections] = useState(false);
  const [showCreateCorrection, setShowCreateCorrection] = useState(false);
  const [eligibleInvoices, setEligibleInvoices] = useState<any[]>([]); // Replace any with InvoiceDto if available
  const [selectedCorrectionInvoice, setSelectedCorrectionInvoice] = useState<
    any | null
  >(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionAmount, setCorrectionAmount] = useState<number>(0);
  const [creatingCorrection, setCreatingCorrection] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [loadingEligibleInvoices, setLoadingEligibleInvoices] = useState(false);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState<string>('');

  // Top-level effect for redirect and fetching approval threshold
  useEffect(() => {
    if (!invoice) {
      setCurrentStep && setCurrentStep('company-selection');
      setShouldRedirect(true);
    } else {
      let mounted = true;
      setThresholdLoading(true);
      getApprovalThreshold()
        .then((val) => {
          if (mounted) setApprovalThreshold(val);
        })
        .catch(() => {
          if (mounted) setApprovalThreshold(10000);
        })
        .finally(() => {
          if (mounted) setThresholdLoading(false);
        });
      return () => {
        mounted = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, setCurrentStep]);

  const invoiceId = Number(invoice.invoiceId || invoice.id);

  useEffect(() => {
    if (!invoiceId) return;
    setLoadingCorrections(true);
    fetchInvoiceCorrections(invoiceId)
      .then(setCorrections)
      .catch(() => setCorrections([]))
      .finally(() => setLoadingCorrections(false));
  }, [invoiceId]);

  if (shouldRedirect) {
    return (
      <Box mt={4}>
        <Typography color="error" variant="h6">
          {t(
            'billing.invoice_details.no_invoice_selected',
            'Please select an invoice to view details.'
          )}
        </Typography>
      </Box>
    );
  }

  if (!invoice) {
    return null;
  }

  const prtId = selectedCompany?.id;
  const isCreator =
    approverId && invoice.createdBy && approverId === invoice.createdBy;
  const status = invoice.status || 'PENDING_APPROVAL';
  const currentUser = user?.userProfile?.email || '-';
  const companyName = selectedCompany?.name || '-';
  const invoiceDate = invoice.invoiceDate || '-';
  const dueDate = invoice.dueDate || '-';

  const invoiceTotal = invoice.grossTotal != null ? invoice.grossTotal : 0;

  // Calculate sum from billing items for subtotal display
  const calculatedTotal =
    invoice.billingItems?.reduce(
      (sum, item) => sum + (item.grossAmount || 0),
      0
    ) || 0;
  const isAboveThreshold =
    approvalThreshold !== null ? invoiceTotal > approvalThreshold : false;

  const statusChipColor =
    status === 'APPROVED'
      ? 'success'
      : status === 'REJECTED'
      ? 'error'
      : 'warning';

  const handleProceed = () => {
    alert('Proceeding to the next step...');
  };

  if (thresholdLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <Typography>
          {t(
            'billing.invoice_details.loading_threshold',
            'Loading approval threshold...'
          )}
        </Typography>
      </Box>
    );
  }

  const billingItems = invoice.billingItems || [];

  // Utility to generate the invoice PDF as a Blob
  const generateInvoicePdfBlob = async () => {
    const lang = i18n.language || 'de';
    // Ensure the 'invoice' namespace is loaded for the current language
    await i18n.loadNamespaces('invoice');
    // Debug: log translation and resource bundle status
    console.log(
      'PDF t(title):',
      t('title', { ns: 'invoice' }),
      'hasResourceBundle:',
      i18n.hasResourceBundle(lang, 'invoice'),
      'lang:',
      lang
    );
    const companyName = selectedCompany?.name || 'AccsoRail';
    const now = new Date();
    const invoiceDate =
      invoice.invoiceDate ||
      `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1)
        .toString()
        .padStart(2, '0')}.${now.getFullYear()}`;
    const dueDate = invoice.dueDate || '-';
    const invoiceNumber = invoice.invoiceId || invoice.id || '-';
    const customerNumber = '-';
    const vatId = '-';
    const customerReference = '-';
    // Extract request numbers from billing items
    console.log('Billing items for PDF:', invoice.billingItems);
    const requestNumbers =
      [
        ...new Set(
          (invoice.billingItems || [])
            .map((item) => {
              console.log('Billing item order:', item.order);
              return item.order?.request?.referenceId;
            })
            .filter(Boolean)
        )
      ].join(', ') || '-';
    console.log('Extracted request numbers:', requestNumbers);
    const zugnummer = '-';
    // Header and contact info
    const companyAddress = t('company_address', {
      ns: 'invoice',
      defaultValue: 'Accso GmbH\nHilpertstraße 12 · 64295 Darmstadt'
    });
    const contactInfo = t('contact_info', {
      ns: 'invoice',
      defaultValue:
        'Bei Rückfragen:\nAccso - Accelerated Solutions\nTel.: +49 6151 13029-0\nFax: +49 6151 13029-10\nWeb: www.accsorail.com\nMail: support@accsorail.com'
    });
    // Table data with German descriptions
    const detailedItems = (invoice.billingItems || []).map((item, idx) => {
      let germanDescription = item.description || '-';
      // Convert English descriptions to German
      if (germanDescription.includes('Processing Fee - Sammelauftrag')) {
        germanDescription = 'Bearbeitungsgebühr - Sammelauftrag';
      } else if (germanDescription.includes('Usage Fee - Track Utilization')) {
        germanDescription = 'Nutzungsgebühr - Gleisnutzung';
      } else if (germanDescription.includes('Processing Fee')) {
        germanDescription = germanDescription.replace(
          'Processing Fee',
          'Bearbeitungsgebühr'
        );
      } else if (germanDescription.includes('Usage Fee')) {
        germanDescription = germanDescription.replace(
          'Usage Fee',
          'Nutzungsgebühr'
        );
      } else if (germanDescription.includes('Track Utilization')) {
        germanDescription = germanDescription.replace(
          'Track Utilization',
          'Gleisnutzung'
        );
      }

      return [
        `${idx + 1}`,
        germanDescription,
        item.servicePeriodFrom
          ? new Date(item.servicePeriodFrom).toLocaleDateString(lang)
          : '',
        '-', // No zugnummer field
        '-', // No extra description field
        item.quantity ?? 1,
        item.unitPrice != null
          ? `${item.unitPrice.toLocaleString(lang, {
              minimumFractionDigits: 2
            })} €`
          : '-',
        item.vatRate != null
          ? `${item.vatRate.toLocaleString(lang, {
              minimumFractionDigits: 2
            })}%`
          : '-',
        item.grossAmount != null
          ? `${item.grossAmount.toLocaleString(lang, {
              minimumFractionDigits: 2
            })} €`
          : '-'
      ];
    });
    // Totals - use invoice level totals for accuracy
    const netTotal = (invoice.billingItems || []).reduce(
      (sum, item) => sum + (item.grossAmount || 0),
      0
    ); // Items are actually net amounts
    const vatTotal = invoiceTotal - netTotal; // VAT calculated at invoice level
    const grossTotal = invoiceTotal; // Use invoice.grossTotal which includes all taxes
    // Static legal/payment/bank info
    const legalNote = t('legal_note', {
      ns: 'invoice',
      defaultValue:
        'wichtige Anmerkung:\nSollte eine Zugnummer fehlen oder nicht korrekt durch das EVU bzw. die anmeldende Stelle im System angegeben und ggf. nachgemeldet worden sein, kann dieses auf der Rechnung auch nur entsprechend der getätigten bzw. nicht-getätigten Eingabe wiedergegeben werden. D.h. vermeiden Sie für eine Rechnungsprüfung nach Möglichkeit (künftig) Fehleingaben.'
    });
    const paymentNote = t('payment_note', {
      ns: 'invoice',
      defaultValue:
        'Hinweis:\nZahlungsbedingungen: 21 Tage Netto\nDie Rechnung ist zahlbar bis zum 18.11.2024 auf unser unten aufgeführtes Konto.\nBitte geben Sie bei allen Zahlungen die Rechnungs- und Kundennummer an.\nWenn in den Positionen nicht anders vermerkt, ist der Liefer-/Leistungsmonat September 2024.'
    });
    const bankInfo = t('bank_info', {
      ns: 'invoice',
      defaultValue:
        'Bankverbindung:\nNordwest Bank eG\nIBAN: DE89 2809 5000 1234 5678 90\nBIC: GENODEF1OL2'
    });
    // PDF definition
    const docDefinition = {
      content: [
        // Header
        {
          columns: [
            {
              width: '*',
              stack: [
                {
                  text: companyAddress,
                  fontSize: 8,
                  margin: [0, 40, 0, 0],
                  alignment: 'left'
                }
              ],
              alignment: 'left',
              margin: [0, 0, 0, 0]
            },
            {
              width: 'auto',
              stack: [
                {
                  image: invoiceLogoBase64,
                  width: 100,
                  alignment: 'right',
                  margin: [0, 0, 0, 2]
                },
                {
                  text: contactInfo,
                  fontSize: 8,
                  alignment: 'right',
                  margin: [0, 5, 0, 0]
                }
              ],
              alignment: 'right',
              margin: [0, 0, 0, 0]
            }
          ],
          columnGap: 20,
          margin: [0, 0, 0, 10]
        },
        {
          text: t('title', { ns: 'invoice', defaultValue: 'Rechnung' }),
          style: 'invoiceTitle',
          margin: [0, 0, 0, 8]
        },
        {
          columns: [
            [
              {
                text: [
                  {
                    text: t('number', {
                      ns: 'invoice',
                      defaultValue: 'Rechnung-Nr.: '
                    }),
                    bold: true
                  },
                  `${invoiceNumber}\n`,
                  {
                    text: t('customer_number', {
                      ns: 'invoice',
                      defaultValue: 'Kundennummer: '
                    }),
                    bold: true
                  },
                  `${customerNumber}\n`,
                  {
                    text: t('vat_id', {
                      ns: 'invoice',
                      defaultValue: 'Umsatzsteuer-ID: '
                    }),
                    bold: true
                  },
                  `${vatId}\n`,
                  {
                    text: t('request_number', {
                      ns: 'invoice',
                      defaultValue: 'Auftragsnummer: '
                    }),
                    bold: true
                  },
                  `${requestNumbers}\n`,
                  {
                    text: t('customer_reference', {
                      ns: 'invoice',
                      defaultValue: 'Kundenreferenz: '
                    }),
                    bold: true
                  },
                  `${customerReference}`
                ],
                fontSize: 10,
                margin: [0, 0, 0, 0]
              }
            ],
            [
              {
                text: [
                  {
                    text: t('date', {
                      ns: 'invoice',
                      defaultValue: 'Rechnungsdatum: '
                    }),
                    bold: true
                  },
                  `${invoiceDate}\n`,
                  {
                    text: t('page', { ns: 'invoice', defaultValue: 'Seite: ' }),
                    bold: true
                  },
                  '1/1'
                ],
                alignment: 'right',
                fontSize: 10,
                margin: [0, 0, 0, 0]
              }
            ]
          ]
        },
        { text: ' ', margin: [0, 0, 0, 5] },
        // Table
        {
          table: {
            headerRows: 1,
            widths: [
              'auto',
              '*',
              'auto',
              'auto',
              '*',
              'auto',
              'auto',
              'auto',
              'auto'
            ],
            body: [
              [
                {
                  text: t('position_service', {
                    ns: 'invoice',
                    defaultValue: 'Position Service/Leistungsart/Leistungsdatum'
                  }),
                  style: 'tableHeader',
                  colSpan: 5
                },
                {},
                {},
                {},
                {},
                {
                  text: t('quantity', { ns: 'invoice', defaultValue: 'Menge' }),
                  style: 'tableHeader'
                },
                {
                  text: t('unit_price', {
                    ns: 'invoice',
                    defaultValue: 'Einzelpreis'
                  }),
                  style: 'tableHeader'
                },
                {
                  text: t('vat', { ns: 'invoice', defaultValue: 'MwSt' }),
                  style: 'tableHeader'
                },
                {
                  text: t('amount', { ns: 'invoice', defaultValue: 'Betrag' }),
                  style: 'tableHeader'
                }
              ],
              ...detailedItems
            ]
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? '#E0E0E0' : null),
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#bbb',
            vLineColor: () => '#bbb',
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 4,
            paddingBottom: () => 4
          },
          margin: [0, 0, 0, 10]
        },
        // Summary Section
        {
          text: t('summary', {
            ns: 'invoice',
            defaultValue: 'Zusammenfassung'
          }),
          style: 'tableHeader',
          margin: [0, 5, 0, 3]
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                {
                  text: t('description', {
                    ns: 'invoice',
                    defaultValue: 'Beschreibung'
                  }),
                  style: 'tableHeader'
                },
                {
                  text: t('quantity', { ns: 'invoice', defaultValue: 'Menge' }),
                  style: 'tableHeader'
                },
                {
                  text: t('unit_price', {
                    ns: 'invoice',
                    defaultValue: 'Einzelpreis'
                  }),
                  style: 'tableHeader'
                },
                {
                  text: t('amount', { ns: 'invoice', defaultValue: 'Betrag' }),
                  style: 'tableHeader'
                }
              ],
              ...detailedItems.map((item) => [
                item[1],
                item[5],
                item[6],
                item[8]
              ]),
              [
                {
                  text: t('net_total', {
                    ns: 'invoice',
                    defaultValue: 'Gesamtbetrag Netto'
                  }),
                  colSpan: 3,
                  alignment: 'right',
                  bold: true
                },
                {},
                {},
                {
                  text: `${netTotal.toLocaleString(lang, {
                    minimumFractionDigits: 2
                  })} €`,
                  alignment: 'right',
                  bold: true
                }
              ],
              [
                {
                  text: t('vat_total', {
                    ns: 'invoice',
                    defaultValue: '19,00 % UST'
                  }),
                  colSpan: 3,
                  alignment: 'right',
                  bold: true
                },
                {},
                {},
                {
                  text: `${vatTotal.toLocaleString(lang, {
                    minimumFractionDigits: 2
                  })} €`,
                  alignment: 'right',
                  bold: true
                }
              ],
              [
                {
                  text: t('gross_total', {
                    ns: 'invoice',
                    defaultValue: 'Gesamtbetrag'
                  }),
                  colSpan: 3,
                  alignment: 'right',
                  bold: true
                },
                {},
                {},
                {
                  text: `${grossTotal.toLocaleString(lang, {
                    minimumFractionDigits: 2
                  })} €`,
                  alignment: 'right',
                  bold: true
                }
              ]
            ]
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? '#E0E0E0' : null),
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#bbb',
            vLineColor: () => '#bbb',
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 4,
            paddingBottom: () => 4
          },
          margin: [0, 0, 0, 10]
        },
        // Legal/Payment/Bank Info
        {
          text: t('legal_note', { ns: 'invoice', defaultValue: legalNote }),
          fontSize: 8,
          margin: [0, 5, 0, 2]
        },
        {
          text: t('payment_note', { ns: 'invoice', defaultValue: paymentNote }),
          fontSize: 8,
          margin: [0, 2, 0, 2]
        },
        {
          text: t('bank_info', { ns: 'invoice', defaultValue: bankInfo }),
          fontSize: 8,
          margin: [0, 2, 0, 2]
        }
      ],
      styles: {
        invoiceTitle: {
          fontSize: 16,
          bold: true,
          color: '#174B7D',
          alignment: 'left',
          margin: [0, 0, 0, 8]
        },
        tableHeader: {
          bold: true,
          fontSize: 10,
          color: 'black',
          fillColor: '#E0E0E0'
        }
      },
      defaultStyle: {
        fontSize: 9
      },
      pageMargins: [30, 40, 30, 40]
    };
    return new Promise<Blob>((resolve) => {
      pdfMake.createPdf(docDefinition as any).getBlob((blob: Blob) => {
        resolve(blob);
      });
    });
  };

  const handleOpenEmailDialog = () => {
    // If company email is added in the future, use it here. For now, fallback to user email.
    setRecipientEmail(
      selectedCompany && (selectedCompany as any).email
        ? (selectedCompany as any).email
        : user?.userProfile?.email || ''
    );
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmailError(null);
  };

  const handleSendInvoiceEmail = async () => {
    if (!prtId || !invoiceId || !recipientEmail) return;
    setEmailLoading(true);
    setEmailError(null);
    try {
      const pdfBlob = await generateInvoicePdfBlob();
      const formData = new FormData();
      formData.append(
        'file',
        pdfBlob,
        `Invoice_${invoice.invoiceId || invoice.id}.pdf`
      );
      formData.append('recipient', recipientEmail);
      await emailInvoice(prtId, String(invoiceId), formData);
      setSnackbar({
        open: true,
        message: t('Invoice emailed successfully!'),
        severity: 'success'
      });
      setEmailDialogOpen(false);
    } catch (e: any) {
      setEmailError(e?.message || t('Failed to email invoice.'));
      setSnackbar({
        open: true,
        message: t('Failed to email invoice.'),
        severity: 'error'
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Restore handleViewPDF for the UI
  const handleViewPDF = async () => {
    const pdfBlob = await generateInvoicePdfBlob();
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleDownloadPDF = async () => {
    const pdfBlob = await generateInvoicePdfBlob();
    const fileId = invoice.invoiceId || invoice.id;
    const now = new Date();
    const fileDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(
      2,
      '0'
    )}${String(now.getDate()).padStart(2, '0')}`;
    const filename = `Invoice_${fileId}_${fileDate}.pdf`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(pdfBlob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    }, 100);
  };

  return (
    <Box mt={4}>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <>
              {t('billing.invoice_details.title', 'Invoice Details')} -{' '}
              {invoice.invoiceId || invoice.id}
              <Chip
                label={
                  status === 'APPROVED'
                    ? t('billingItems.status.approved')
                    : status === 'REJECTED'
                    ? t('billingItems.status.rejected')
                    : t('billingItems.status.pending_approval')
                }
                color={statusChipColor}
                sx={{ ml: 2, fontWeight: 'bold', fontSize: 16 }}
              />
            </>
          }
          subheader={
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <strong>
                    {t('billing.invoice_details.created_by', 'Created By')}:
                  </strong>{' '}
                  {invoice.createdByName || invoice.createdBy || '-'}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <strong>
                    {t('billing.invoice_details.current_user', 'Current User')}:
                  </strong>{' '}
                  {currentUser}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <strong>
                    {t('billing.invoice_details.company_name', 'Company Name')}:
                  </strong>{' '}
                  {companyName}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <strong>
                    {t(
                      'billing.invoice_details.approval_threshold',
                      'Approval Threshold'
                    )}
                    :
                  </strong>{' '}
                  €
                  {approvalThreshold != null
                    ? approvalThreshold.toLocaleString()
                    : '-'}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <strong>
                    {t('billing.invoice_details.invoice_date', 'Invoice Date')}:
                  </strong>{' '}
                  {invoiceDate}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <strong>
                    {t('billing.invoice_details.due_date', 'Due Date')}:
                  </strong>{' '}
                  {dueDate}
                </Grid>
              </Grid>
            </>
          }
        />
        <Divider />
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <strong>
              {t('billing.invoice_details.total_amount', 'Total Amount:')}
            </strong>
            <span
              style={{
                color: isAboveThreshold ? '#dc3545' : undefined,
                marginLeft: 8
              }}
            >
              €
              {invoiceTotal.toLocaleString(undefined, {
                minimumFractionDigits: 2
              })}
            </span>
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('billing.invoice_details.billing_items', 'Billing Items')}
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>
                    {t('billing.invoice_details.table.invoice_id')}
                  </TableCell>
                  <TableCell>
                    {t('billing.invoice_details.table.description')}
                  </TableCell>
                  <TableCell align="right">
                    {t('billing.invoice_details.table.amount_gross')}
                  </TableCell>
                  <TableCell>
                    {t('billing.invoice_details.table.status')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(invoice.billingItems || []).map(
                  (
                    item: import('../context/BillingInvoicingContext').BillingItem,
                    idx: number
                  ) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        backgroundColor: idx % 2 === 0 ? '#fafbfc' : undefined
                      }}
                    >
                      <TableCell>{item.id}</TableCell>
                      <TableCell>
                        {item.description ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: formatBillingItemDescription(
                                item.description
                              )
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        €
                        {item.grossAmount != null
                          ? item.grossAmount.toLocaleString(undefined, {
                              minimumFractionDigits: 2
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {item.status === 'APPROVED'
                          ? t('billingItems.status.approved')
                          : item.status === 'REJECTED'
                          ? t('billingItems.status.rejected')
                          : item.status === 'PENDING_APPROVAL'
                          ? t('billingItems.status.pending_approval')
                          : item.status || '-'}
                      </TableCell>
                    </TableRow>
                  )
                )}
                {/* Subtotal row */}
                <TableRow>
                  <TableCell colSpan={2} align="right">
                    {t('billing.invoice_details.table.subtotal', 'Subtotal')}
                  </TableCell>
                  <TableCell align="right">
                    €
                    {calculatedTotal.toLocaleString(undefined, {
                      minimumFractionDigits: 2
                    })}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
                {/* VAT row */}
                {invoiceTotal > calculatedTotal && (
                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      {t('billing.invoice_details.table.vat', 'VAT (19%)')}
                    </TableCell>
                    <TableCell align="right">
                      €
                      {(invoiceTotal - calculatedTotal).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2
                        }
                      )}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
                {/* Total row */}
                <TableRow>
                  <TableCell colSpan={2} align="right">
                    <strong>
                      {t('billing.invoice_details.table.total', 'Total')}
                    </strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>
                      €
                      {invoiceTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2
                      })}
                    </strong>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, background: '#f9f9fc' }}>
        <CardHeader
          title={t(
            'billing.invoice_details.invoice_approval',
            'Invoice Approval'
          )}
        />
        <Divider />
        <CardContent>
          <Box mb={2}>
            <Chip
              label={
                status === 'APPROVED'
                  ? t('billingItems.status.approved')
                  : status === 'REJECTED'
                  ? t('billingItems.status.rejected')
                  : t('billingItems.status.pending_approval')
              }
              color={statusChipColor}
              sx={{ fontWeight: 'bold', fontSize: 16 }}
            />
          </Box>
          {isCreator && isAboveThreshold && status === 'PENDING_APPROVAL' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(
                'billing.invoice_details.4eyes_error',
                'You cannot approve your own invoice. Assign another user.'
              )}
            </Alert>
          )}
          <TextField
            label={t(
              'billing.invoice_details.optional_comment',
              "Add optional comments (e.g., 'Verified with client')"
            )}
            multiline
            minRows={2}
            fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="success"
              disabled={
                status !== 'PENDING_APPROVAL' ||
                isCreator ||
                !isAboveThreshold ||
                approveLoading
              }
              onClick={() => setConfirmApproveOpen(true)}
              title={
                isCreator
                  ? t(
                      'billing.invoice_details.cannot_approve_own',
                      'You cannot approve your own invoice.'
                    )
                  : !isAboveThreshold
                  ? t(
                      'billing.invoice_details.automatically_approved',
                      'Automatically approved'
                    )
                  : t(
                      'billing.invoice_details.requires_second_user',
                      'Requires second user approval'
                    )
              }
            >
              {approveLoading
                ? t('billing.invoice_details.approving', 'Approving...')
                : t('billing.invoice_details.approve', 'Approve')}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={
                status !== 'PENDING_APPROVAL' ||
                isCreator ||
                !isAboveThreshold ||
                rejectLoading
              }
              onClick={() => setConfirmRejectOpen(true)}
              title={
                isCreator
                  ? t(
                      'billing.invoice_details.cannot_reject_own',
                      'You cannot reject your own invoice.'
                    )
                  : !isAboveThreshold
                  ? t(
                      'billing.invoice_details.automatically_approved',
                      'Automatically approved'
                    )
                  : t(
                      'billing.invoice_details.requires_second_user',
                      'Requires second user approval'
                    )
              }
            >
              {rejectLoading
                ? t('billing.invoice_details.rejecting', 'Rejecting...')
                : t('billing.invoice_details.reject', 'Reject')}
            </Button>
            <Button variant="outlined" onClick={handleViewPDF}>
              {t('billing.invoice_details.view_pdf', 'View PDF')}
            </Button>
            <Button variant="outlined" onClick={handleDownloadPDF}>
              {t('billing.invoice_details.download_pdf', 'Download PDF')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenEmailDialog}
              disabled={emailLoading}
            >
              {emailLoading
                ? t('billing.invoice_details.emailing', 'Emailing...')
                : t('billing.invoice_details.email_invoice', 'Email Invoice')}
            </Button>
            {/*
            {status === 'APPROVED' && false && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleFinalize}
                disabled={finalizeLoading}
                sx={{ mt: 2, mr: 2 }}
              >
                {finalizeLoading ? t('Finalizing...') : t('Finalize Invoice')}
              </Button>
            )}
            {status === 'FINALIZED' && false && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSend}
                disabled={sendLoading}
                sx={{ mt: 2, mr: 2 }}
              >
                {sendLoading ? t('Sending...') : t('Send Invoice')}
              </Button>
            )}
            */}
          </Stack>
          {finalizeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {finalizeError}
            </Alert>
          )}
          {sendError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {sendError}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={t('billing.invoice_details.audit_log', 'Audit Log')}
        />
        <Divider />
        <CardContent>
          {Array.isArray(invoice.auditLog) && invoice.auditLog.length > 0 ? (
            <>
              {invoice.auditLog.map((entry, idx) => (
                <Box key={idx} mb={2}>
                  <Typography variant="subtitle2" color="text.primary">
                    {t(
                      `billing.invoice_details.audit_action.${entry.action}`,
                      entry.action
                    )}{' '}
                    {t('billing.invoice_details.by_user_on', {
                      user: entry.userName,
                      date: new Date(entry.timestamp).toLocaleString(),
                      defaultValue: 'by {{user}} on {{date}}'
                    })}
                  </Typography>
                  {entry.comment && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {entry.comment}
                    </Typography>
                  )}
                </Box>
              ))}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t(
                'billing.invoice_details.no_audit_log',
                'No audit log entries yet.'
              )}
            </Typography>
          )}
        </CardContent>
      </Card>

      {approveError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {approveError.includes('4-eyes principle')
            ? t(
                'billing.invoice_details.4eyes_error',
                'Approval failed: You cannot approve an invoice you created. Please ask another authorized user to approve it.'
              )
            : approveError}
        </Alert>
      )}
      {rejectError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {rejectError}
        </Alert>
      )}
      <Dialog
        open={confirmApproveOpen}
        onClose={() => setConfirmApproveOpen(false)}
      >
        <DialogTitle>
          {t(
            'billing.invoice_details.confirm_approve_title',
            'Confirm Approval'
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'billing.invoice_details.confirm_approve_text',
              'Are you sure you want to approve this invoice?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveOpen(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={async () => {
              if (prtId && invoiceId && approverId)
                await approveInvoice(
                  prtId,
                  String(invoiceId),
                  approverId,
                  rejectReason
                );
              setConfirmApproveOpen(false);
            }}
            color="success"
            disabled={approveLoading || !invoiceId || !prtId || !approverId}
          >
            {t('billing.invoice_details.approve', 'Approve')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmRejectOpen}
        onClose={() => setConfirmRejectOpen(false)}
      >
        <DialogTitle>
          {t(
            'billing.invoice_details.confirm_reject_title',
            'Confirm Rejection'
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'billing.invoice_details.confirm_reject_text',
              'Please provide a reason for rejection.'
            )}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('billing.invoice_details.reject_reason', 'Reason')}
            type="text"
            fullWidth
            variant="standard"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={rejectLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRejectOpen(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={async () => {
              if (prtId && invoiceId && approverId)
                await rejectInvoice(
                  prtId,
                  String(invoiceId),
                  approverId,
                  rejectReason
                );
              setConfirmRejectOpen(false);
              setRejectReason('');
            }}
            color="error"
            disabled={rejectLoading || !rejectReason.trim() || !invoiceId}
          >
            {t('billing.invoice_details.reject', 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Corrections Section */}
      {/*
      <Box mt={4}>
        <Typography variant="h6">{t('Invoice Corrections')}</Typography>
        {loadingCorrections ? <Typography>{t('Loading...')}</Typography> : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Correction Invoice')}</TableCell>
                  <TableCell>{t('Reason')}</TableCell>
                  <TableCell>{t('Amount')}</TableCell>
                  <TableCell>{t('Date')}</TableCell>
                  <TableCell>{t('Created By')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {corrections.map(corr => (
                  <TableRow key={corr.invoiceCorrectionId}>
                    <TableCell>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => navigate(`/billing-invoicing?invoiceId=${corr.correctionInvoiceId}`)}
                      >
                        {corr.correctionInvoiceId}
                      </Button>
                    </TableCell>
                    <TableCell>{corr.correctionReason}</TableCell>
                    <TableCell>{corr.correctionAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell>{new Date(corr.creationDate).toLocaleString()}</TableCell>
                    <TableCell>{corr.createdBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Button
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleOpenCreateCorrection}
          disabled={status !== 'FINALIZED' && status !== 'SENT'}
        >
          {t('Create Invoice Correction')}
        </Button>
      </Box>
      */}
      {/* Create Correction Dialog */}
      {/*
      <Dialog open={showCreateCorrection} onClose={handleCloseCreateCorrection} maxWidth="sm" fullWidth>
        <DialogTitle>{t('Create Invoice Correction')}</DialogTitle>
        <DialogContent>
          {loadingEligibleInvoices ? (
            <Typography>{t('Loading eligible invoices...')}</Typography>
          ) : (
            <Autocomplete
              options={eligibleInvoices}
              getOptionLabel={inv => `${inv.invoiceNumber} (ID: ${inv.invoiceId}, €${inv.grossTotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })})`}
              value={selectedCorrectionInvoice}
              onChange={(_, value) => setSelectedCorrectionInvoice(value)}
              renderInput={params => <TextField {...params} label={t('Correction Invoice')} fullWidth sx={{ mt: 2 }} />}
              isOptionEqualToValue={(option, value) => option.invoiceId === value?.invoiceId}
            />
          )}
          <TextField
            label={t('Reason')}
            value={correctionReason}
            onChange={e => setCorrectionReason(e.target.value)}
            fullWidth
            multiline
            sx={{ mt: 2 }}
          />
          <TextField
            label={t('Amount')}
            type="number"
            value={correctionAmount}
            onChange={e => setCorrectionAmount(Number(e.target.value))}
            fullWidth
            sx={{ mt: 2 }}
          />
          {correctionError && <Alert severity="error" sx={{ mt: 2 }}>{correctionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateCorrection}>{t('Cancel')}</Button>
          <Button onClick={handleCreateCorrection} disabled={creatingCorrection || !selectedCorrectionInvoice || !correctionReason.trim() || !correctionAmount || correctionAmount <= 0} variant="contained" color="primary">
            {creatingCorrection ? t('Creating...') : t('Create')}
          </Button>
        </DialogActions>
      </Dialog>
      */}
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog}>
        <DialogTitle>
          {t(
            'billing.invoice_details.send_invoice_email',
            'Send Invoice via Email'
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'billing.invoice_details.specify_recipient',
              'Please specify the recipient email address for this invoice.'
            )}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t(
              'billing.invoice_details.recipient_email',
              'Recipient Email'
            )}
            type="email"
            fullWidth
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            disabled={emailLoading}
          />
          {emailError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {emailError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSendInvoiceEmail}
            color="primary"
            variant="contained"
            disabled={emailLoading || !recipientEmail}
          >
            {emailLoading
              ? t('billing.invoice_details.sending', 'Sending...')
              : t('billing.invoice_details.send', 'Send')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          style: {
            backgroundColor:
              snackbar.severity === 'success' ? '#43a047' : '#d32f2f',
            color: '#fff'
          }
        }}
      />
    </Box>
  );
};

export default InvoiceDetails;
